import { useLazyQuery } from '@apollo/client'
import { DateTime } from 'luxon'
import { BookingGetDailyPriceQuery, BookingGetDailyPriceQueryVariables } from 'parkdepot-shared/gql/graphql'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { CheckoutContext } from '../../context/CheckoutContext/checkout.context'
import { LotContext } from '../../context/LotContext/lot.context'
import { GET_DAILY_PRICE } from '../../graphql/queries/getDailyPrice.query'
import { useGetUser } from '../../hooks/useAuth'
import datetimeToStartOrEndDatetime from '../../utils/datetimeToStartOrEndDatetime.utils'
import { getLotPricingMemoized } from '../../utils/getLotPricing.utils'
import DailyBooking from './components/DailyBooking/DailyBooking.component'
import LongDurationTempPlaceholder from './style/LongDurationTempPlaceholder.style'
import { generateDailyPriceQueryOptions } from './utils/generateDailyPriceQueryOptions.utils'
import { getIsoDurationInDays } from '../../utils/getMinOrMaxFromISODuration.utils'

const LongDuration = () => {
    const [, setDatepickerOpen] = useState(false)
    const [datepickerStartDate, setDatepickerStartDate] = useState<Date | null>(null)
    const [datepickerEndDate, setDatepickerEndDate] = useState<Date | null>(null)
    const [initialUrlChecked, setInitialUrlChecked] = useState(false)
    const isMounted = useRef(false)

    const location = useLocation()
    const history = useHistory()
    const { user } = useGetUser()
    const { modifyAndSetDailyCheckout } = useContext(CheckoutContext)
    const { lot, setNextButtonLoading, setNextDisabled } = useContext(LotContext)

    const [getDailyPrice] = useLazyQuery<BookingGetDailyPriceQuery, BookingGetDailyPriceQueryVariables>(GET_DAILY_PRICE)

    const updateUrlWithDaysDates = useCallback(
        (startDate: string, endDate: string) => {
            const params = new URLSearchParams(location.search)
            params.set('start', startDate)
            params.set('end', endDate)

            history.replace({
                pathname: location.pathname,
                search: params.toString()
            })
        },
        [history, location.pathname]
    )

    const onDatesChange = useCallback(
        async (startDate: Date | null, endDate: Date | null) => {
            if (!lot || !startDate || !endDate) return

            if (startDate !== null && endDate !== null) {
                setDatepickerStartDate(startDate)
                setDatepickerEndDate(endDate)
                setDatepickerOpen(false)
                setNextButtonLoading(true)
                const start = datetimeToStartOrEndDatetime(startDate)
                const end = datetimeToStartOrEndDatetime(endDate, false)
                const privatePricingLot = getLotPricingMemoized(user, lot.id)

                try {
                    const { data: priceData, error } = await getDailyPrice(
                        generateDailyPriceQueryOptions(lot.id, start, end, privatePricingLot.pricing_type)
                    )
                    if (!isMounted.current) return
                    if (lot) {
                        modifyAndSetDailyCheckout(lot, priceData?.booking_get_price?.price || 0, start, end)
                        setNextDisabled(false)
                        updateUrlWithDaysDates(start.toFormat('dd-MM-yyyy'), end.toFormat('dd-MM-yyyy'))
                    }
                    if (error) {
                        // TODO: handle error, depends how BE returns it
                    }
                } finally {
                    setNextButtonLoading(false)
                }
            }
        },
        [
            lot,
            user,
            modifyAndSetDailyCheckout,
            setNextButtonLoading,
            setNextDisabled,
            getDailyPrice,
            updateUrlWithDaysDates
        ]
    )

    useEffect(() => {
        if (initialUrlChecked) return
        const urlSearchParams = new URLSearchParams(location.search)
        const startDate = urlSearchParams.get('start')
        const endDate = urlSearchParams.get('end')

        const earliest_booking_date = lot?.earliest_booking_date ? new Date(lot.earliest_booking_date) : new Date()
        const latest_booking_date = new Date(lot?.end_of_live_date ?? '9999-12-31')
        const minBookingDays = getIsoDurationInDays(lot?.min_booking_duration ?? 'P1D')
        const maxBookingDays = getIsoDurationInDays(lot?.max_booking_duration ?? 'P365D')

        const startDateAsDate = startDate ? DateTime.fromFormat(startDate, 'dd-MM-yyyy').toJSDate() : new Date()
        const endDateAsDate = endDate ? DateTime.fromFormat(endDate, 'dd-MM-yyyy').toJSDate() : new Date()

        const numberOfDaysBetweenDates =
            Math.abs(endDateAsDate.getTime() - startDateAsDate.getTime()) / (1000 * 60 * 60 * 24)

        if (
            startDate &&
            endDate &&
            startDateAsDate > earliest_booking_date &&
            startDateAsDate < latest_booking_date &&
            endDateAsDate > earliest_booking_date &&
            endDateAsDate < latest_booking_date &&
            endDateAsDate >= startDateAsDate &&
            numberOfDaysBetweenDates >= minBookingDays &&
            numberOfDaysBetweenDates <= maxBookingDays
        ) {
            try {
                const startDT = DateTime.fromFormat(startDate, 'dd-MM-yyyy').toJSDate()
                const endDT = DateTime.fromFormat(endDate, 'dd-MM-yyyy').toJSDate()
                const today = new Date()
                today.setHours(0, 0, 0, 0)

                // if date from url is larger than today or start date is larger than end date
                if (today > startDT || today > endDT || startDT > endDT) {
                    setInitialUrlChecked(true)
                    return
                }

                // if date from url is valid
                if (!isNaN(startDT.getTime()) && !isNaN(endDT.getTime())) {
                    setDatepickerStartDate(startDT)
                    setDatepickerEndDate(endDT)
                    onDatesChange(startDT, endDT)
                }
            } catch (error) {
                console.error('Error parsing dates from URL', error)
            } finally {
                setInitialUrlChecked(true)
            }
        } else {
            setInitialUrlChecked(true)
        }
    }, [location.search, lot, initialUrlChecked, onDatesChange])

    useEffect(() => {
        isMounted.current = true
        return () => {
            isMounted.current = false
        }
    }, [])

    if (!lot) return null

    if (initialUrlChecked) {
        return (
            <DailyBooking
                lot={lot}
                startDate={datepickerStartDate}
                endDate={datepickerEndDate}
                datepickerVisibilityChange={setDatepickerOpen}
                onDatesChanges={onDatesChange}
            />
        )
    }

    return <LongDurationTempPlaceholder />
}

export default LongDuration
